import { useEffect, useRef } from 'react';

import { GET_ONBOARDING_V2_VARIANT } from '@/features/flags';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * Fires `screen_viewed` once per mount for a guided-tour screen, matching the
 * other onboarding screens (`onboarding_privacy`, `onboarding_invite_team`) so
 * the treatment's screens sit in the same funnel the control arm reports into.
 *
 * Shared by `TourStepShell` and `TourTranscriptionStep`: the transcription
 * screen needs its own footer and CTA copy so it can't reuse the shell, but it
 * is the first screen of the tour funnel and has to report a view.
 */
export function useTourScreenView(screenName: ScreenName) {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    // `track()` rather than `dispatch.analyticsScribe.trackScreenViewed` used by
    // the older sibling steps: same payload, and the Rematch ratchet freezes new
    // dispatch call sites.
    track({
      event_name: TrackingEventName.ScreenViewed,
      props: {
        screen_name: screenName,
        onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
        // These screens render in the treatment arm only, so the arm is a
        // constant here rather than something to thread down from the provider.
        onboarding_sub_variant: 'guided_tour',
      },
    });
  }, [screenName]);
}
